import React from 'react';
import styled from 'styled-components';
import { renderContent, renderContentWithParams } from 'utilities/content/content-utilities';
import { TypographyComponent as TypographyElement, Icon } from '@tvg/fuse-design';
import IconRestore from '@tvg/fuse-icons/lib/Restore';
import IconCoins from '@tvg/fuse-icons/lib/Coins';
import IconTakeThreeSmall from '@tvg/fuse-icons/lib/TakeThreeSmall';
import IconChevronRight from '@tvg/fuse-icons/lib/ChevronRight';
import IconCalendarMonth from '@tvg/fuse-icons/lib/CalendarMonth';

import { BnplCreditOffer, CreditOfferTypes, StcCreditOffer, Take3CreditOffer } from 'types/CreditOfferTypes';
import CardButton from 'components/CardButton/CardButton';
import { DialogText, AprText } from '../../styles/VeryPayDialogStyles';
import PaymentOptionsHeader from './PaymentOptionsHeader';

const Container = styled.div`
  height: 100%;
  overflow: auto;

  &.exit-done {
    visibility: hidden;
  }
`;

const WaysToPay = styled.div`
  display: flex;
  min-height: 120px;
  padding: 0 var(--spacing-space16);
  justify-content: center;
  align-items: center;
  background-color: var(--color-brand-light);
`;

const OptionList = styled.div`
  padding: var(--spacing-space16) 0;
  display: flex;
  flex-direction: column;
`;

const ChevroneContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  width: 56px;
  height: 100%;
  right: 0;
  top: 0;
`;
const CreditOption = styled(CardButton)`
  display: flex;
  padding: var(--spacing-space16) 56px var(--spacing-space16) var(--spacing-space16);
`;

const CardOption = styled.div`
  display: flex;
  padding: var(--spacing-space16);
`;

const FuseIcon = styled(Icon)``;

const OptionIcon = styled.div`
  display: flex;
  flex: 0 0 32px;
  justify-content: center;
  margin-right: var(--spacing-space8);
  position: relative;
  top: 2px;
  ${FuseIcon} {
    background-color: var(--color-brand-mid);
  }
`;

const OptionBody = styled.span`
  flex: 1;

  > * {
    display: block;
  }

  > *:only-child {
    margin-top: var(--spacing-space4);
  }
`;

const StyledApr = styled(AprText)`
  text-align: left;
  padding: var(--spacing-space16) var(--spacing-space24);
`;

type Props = {
  apr: string;
  bnpl: BnplCreditOffer;
  stc: StcCreditOffer;
  take3: Take3CreditOffer;
  nonCreditAccountUser: boolean;
  setActivePage: (panel: string) => void;
};

function PaymentOptionsPage(props: Props) {
  const { bnpl, stc, take3, nonCreditAccountUser, apr, setActivePage } = props;

  return (
    <Container data-testid="verypay_dialog_payment-options">
      <WaysToPay>
        <PaymentOptionsHeader />
      </WaysToPay>
      <OptionList>
        {bnpl && (
          <CreditOption
            aria-label={`Go to ${renderContent('verypay.dialog.options.bnpl.heading')} section`}
            onClick={() => setActivePage(CreditOfferTypes.BNPL)}
            data-testid="verypay_dialog_payment-options_bnpl"
          >
            <OptionIcon>
              <FuseIcon icon={<IconRestore />} size="large" width="28px" height="28px" />
            </OptionIcon>

            <OptionBody>
              <TypographyElement tag="span" size="1">
                {renderContent('verypay.dialog.options.bnpl.heading')}
              </TypographyElement>
            </OptionBody>
            <ChevroneContainer tabIndex={0}>
              <Icon width="32px" height="32px" icon={<IconChevronRight />} />
            </ChevroneContainer>
          </CreditOption>
        )}

        {take3 && (
          <CreditOption
            aria-label={`Go to ${renderContent('verypay.dialog.options.take3.heading')} section`}
            onClick={() => setActivePage(CreditOfferTypes.TAKE3)}
            data-testid="verypay_dialog_payment-options_take3"
          >
            <OptionIcon>
              <FuseIcon
                icon={<IconTakeThreeSmall />}
                width="24px"
                height="24px"
                alt={renderContent('verypay.dialog.options.take3.heading')}
              />
            </OptionIcon>

            <OptionBody>
              <TypographyElement tag="span" size="1">
                {renderContent('verypay.dialog.options.take3.heading')}
              </TypographyElement>
              <DialogText
                tag="span"
                dangerouslySetInnerHTML={{
                  __html: renderContentWithParams({
                    id: 'verypay.dialog.options.take3.description',
                    params: [take3.attributes?.firstPayment],
                  }),
                }}
              />
            </OptionBody>
            <ChevroneContainer tabIndex={0}>
              <Icon width="32px" height="32px" icon={<IconChevronRight />} />
            </ChevroneContainer>
          </CreditOption>
        )}

        {stc && (
          <CreditOption
            aria-label={`Go to ${renderContent('verypay.dialog.options.stc.heading')} section`}
            onClick={() => setActivePage(CreditOfferTypes.STC)}
            data-testid="verypay_dialog_payment-options_stc"
          >
            <OptionIcon>
              <FuseIcon
                icon={<IconCalendarMonth />}
                width="24px"
                height="24px"
                alt={renderContent('verypay.dialog.options.stc.heading')}
              />
            </OptionIcon>

            <OptionBody>
              <TypographyElement tag="span" size="1">
                {renderContent('verypay.dialog.options.stc.heading')}
              </TypographyElement>
              <DialogText tag="span">{renderContent('verypay.dialog.options.stc.description')}</DialogText>
            </OptionBody>
            <ChevroneContainer tabIndex={0}>
              <Icon width="32px" height="32px" icon={<IconChevronRight />} />
            </ChevroneContainer>
          </CreditOption>
        )}

        <CardOption data-testid="verypay_dialog_payment-options_pay-by-card">
          <OptionIcon>
            <FuseIcon
              icon={<IconCoins />}
              width="24px"
              height="24px"
              alt={renderContent('verypay.dialog.options.card.heading')}
            />
          </OptionIcon>

          <OptionBody>
            <TypographyElement tag="h3" size="1">
              {renderContent('verypay.dialog.options.card.heading')}
            </TypographyElement>
            <DialogText>{renderContent('verypay.dialog.options.card.description')}</DialogText>
          </OptionBody>
        </CardOption>

        {apr && nonCreditAccountUser && (
          <StyledApr>
            {renderContentWithParams({
              id: 'verypay.dialog.options.apr',
              params: [apr],
            })}
          </StyledApr>
        )}
      </OptionList>
    </Container>
  );
}

export default PaymentOptionsPage;
